/**
 * Comprehensive Contract Clause Mapping System
 * Extracts ALL contract specifics submitted by buyers into negotiable clauses
 * This ensures sellers can see and negotiate every aspect of the buyer's offer
 */

import {
  contractCopyOfInspections,
  contractFinancingContingencyTypes,
  contractInspectionContingencies,
  contractInspectionPresenceOptions,
  contractPropertyConditions,
  sellerAssistanceTypes,
} from '@/config/constants';
import { Contract } from '@/types/ContractType';
import {
  ClauseProperties,
  generateClauseIdWithProperties,
} from './clauseIdUtils';

export interface ComprehensiveClauseData {
  id: string;
  hierarchicalId?: string;
  title: string;
  description: string;
  value: any;
  originalValue: any;
  category:
    | 'price'
    | 'timeline'
    | 'contingency'
    | 'inspection'
    | 'financing'
    | 'occupancy'
    | 'property'
    | 'legal'
    | 'other';
  subcategory: string;
  isNegotiable: boolean;
  isRequired: boolean;
  negotiationStatus?: 'original' | 'countered' | 'accepted' | 'rejected';
  proposedBy?: 'buyer' | 'seller' | 'agent';
  parentClauseId?: string;
  subClauses?: ComprehensiveClauseData[];
  properties?: ClauseProperties;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    required?: boolean;
    type?:
      | 'text'
      | 'number'
      | 'currency'
      | 'percentage'
      | 'date'
      | 'multiline'
      | 'boolean';
    options?: Array<string | { value: string; label: string }>;
  };
  source: string; // Which contract section this came from
}

export interface ComprehensiveClauseCategory {
  id: string;
  title: string;
  description: string;
  clauses: ComprehensiveClauseData[];
  isCollapsible: boolean;
  isExpanded: boolean;
  subcategories?: string[];
}

/**
 * Extract ALL negotiable clauses from comprehensive contract data
 */
export function extractComprehensiveContractClauses(
  contract: Contract
): ComprehensiveClauseCategory[] {
  if (!contract) {
    console.warn(
      'extractComprehensiveContractClauses - No contract data provided'
    );
    return [];
  }

  console.log(
    'extractComprehensiveContractClauses - Contract data structure:',
    {
      hasOfferSpecifics: !!contract.offerSpecifics,
      hasTimeline: !!contract.timeline,
      hasContingency: !!contract.contingency,
      hasThirdPartyInspection: !!contract.thirdPartyInspection,
      hasSellerPropertyInfo: !!contract.sellerPropertyInfo,
      hasAgentSellerInfo: !!contract.agentSellerInfo,
      hasSellerPropertyDisclosure: !!contract.sellerPropertyDisclosure,
      contractKeys: Object.keys(contract),
    }
  );

  const categories: ComprehensiveClauseCategory[] = [];

  // Price & Financial Terms Category
  const priceClauses = extractComprehensivePriceClauses(contract);
  if (priceClauses.length > 0) {
    categories.push({
      id: 'price',
      title: 'Price & Financial Terms',
      description:
        'Purchase price, deposits, financing, and all financial aspects',
      clauses: priceClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: [
        'purchase_price',
        'deposits',
        'financing',
        'assistance',
        'commissions',
      ],
    });
  }

  // Timeline & Deadlines Category
  const timelineClauses = extractComprehensiveTimelineClauses(contract);
  if (timelineClauses.length > 0) {
    categories.push({
      id: 'timeline',
      title: 'Timeline & Deadlines',
      description: 'All dates, deadlines, and time-sensitive terms',
      clauses: timelineClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: [
        'closing',
        'deposits',
        'appraisal',
        'financing_commitment',
      ],
    });
  }

  // Contingencies Category
  const contingencyClauses = extractComprehensiveContingencyClauses(contract);
  if (contingencyClauses.length > 0) {
    categories.push({
      id: 'contingency',
      title: 'Contingencies',
      description: 'All contingencies and conditions that must be met',
      clauses: contingencyClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: ['financing', 'appraisal', 'inspection', 'occupancy'],
    });
  }

  // Inspections Category
  const inspectionClauses = extractComprehensiveInspectionClauses(contract);
  if (inspectionClauses.length > 0) {
    categories.push({
      id: 'inspection',
      title: 'Inspections & Property Condition',
      description:
        'All inspection terms, property condition, and related conditions',
      clauses: inspectionClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: [
        'home_inspection',
        'property_condition',
        'inspection_terms',
      ],
    });
  }

  // Occupancy & Possession Category
  const occupancyClauses = extractComprehensiveOccupancyClauses(contract);
  if (occupancyClauses.length > 0) {
    categories.push({
      id: 'occupancy',
      title: 'Occupancy & Possession',
      description: 'Post-closing occupancy, possession, and related terms',
      clauses: occupancyClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: ['post_close_occupancy', 'possession'],
    });
  }

  // Property Details Category
  const propertyClauses = extractComprehensivePropertyClauses(contract);
  if (propertyClauses.length > 0) {
    categories.push({
      id: 'property',
      title: 'Property Details',
      description: 'Property information, personal property, and exclusions',
      clauses: propertyClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: ['personal_property', 'exclusions', 'property_info'],
    });
  }

  // Legal & Documentation Category
  const legalClauses = extractComprehensiveLegalClauses(contract);
  if (legalClauses.length > 0) {
    categories.push({
      id: 'legal',
      title: 'Legal & Documentation',
      description:
        'Legal descriptions, disclosures, and documentation requirements',
      clauses: legalClauses,
      isCollapsible: true,
      isExpanded: true,
      subcategories: ['disclosures', 'legal_description', 'documentation'],
    });
  }

  return categories;
}

/**
 * Extract comprehensive price-related clauses
 */
function extractComprehensivePriceClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Purchase Price — agent listing and/or buyer offer (URLA paths)
  const purchasePrice =
    contract.agentOfferInfo?.offerPrice ??
    contract.offerSpecifics?.offerPrice ??
    (contract.offerSpecifics as { purchasePrice?: number })?.purchasePrice;
  if (purchasePrice != null) {
    clauses.push({
      id: 'purchase-price',
      title: 'Purchase Price',
      description: 'The total purchase price for this property',
      value: purchasePrice,
      originalValue: purchasePrice,
      category: 'price',
      subcategory: 'purchase_price',
      isNegotiable: true,
      isRequired: true,
      source: contract.agentOfferInfo?.offerPrice
        ? 'agentOfferInfo'
        : 'offerSpecifics',
      validation: {
        min: 1,
        required: true,
      },
    });
  }

  // Loan Amount
  if (contract.offerSpecifics?.loanAmount) {
    clauses.push({
      id: 'loan-amount',
      title: 'Loan Amount',
      description: 'The amount to be financed through the loan',
      value: contract.offerSpecifics.loanAmount,
      originalValue: contract.offerSpecifics.loanAmount,
      category: 'price',
      subcategory: 'financing',
      isNegotiable: true,
      isRequired: true,
      source: 'offerSpecifics',
      validation: {
        min: 0,
        required: true,
      },
    });
  }

  // Market Interest Rate
  if (contract.offerSpecifics?.marketInterestRate) {
    const parsedRate = Number(contract.offerSpecifics.marketInterestRate);
    const marketInterestRateValue = Number.isFinite(parsedRate)
      ? parsedRate
      : contract.offerSpecifics.marketInterestRate;
    clauses.push({
      id: 'market-interest-rate',
      title: 'Market Interest Rate',
      description: 'The current market interest rate for the loan',
      value: marketInterestRateValue,
      originalValue: marketInterestRateValue,
      category: 'price',
      subcategory: 'financing',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        min: 0,
        max: 50,
        type: 'percentage',
      },
    });
  }

  // Seller Assistance
  if (contract.offerSpecifics?.sellerAssistanceType) {
    clauses.push({
      id: 'seller-assistance-type',
      title: 'Seller Assistance Type',
      description: 'Type of seller assistance being requested',
      value: contract.offerSpecifics.sellerAssistanceType,
      originalValue: contract.offerSpecifics.sellerAssistanceType,
      category: 'price',
      subcategory: 'assistance',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        options: sellerAssistanceTypes.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  // Seller Assistance Amount
  if (contract.offerSpecifics?.sellerAssistanceClosingCosts) {
    clauses.push({
      id: 'seller-assistance-amount',
      title: 'Seller Assistance Amount',
      description: 'Amount of seller assistance requested',
      value: contract.offerSpecifics.sellerAssistanceClosingCosts,
      originalValue: contract.offerSpecifics.sellerAssistanceClosingCosts,
      category: 'price',
      subcategory: 'assistance',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        min: 0,
      },
    });
  }

  // Buyer Agent Compensation
  if (contract.offerSpecifics?.buyerAgentCompensation) {
    clauses.push({
      id: 'buyer-agent-compensation',
      title: 'Buyer Agent Compensation',
      description: "Commission percentage for buyer's agent",
      value: contract.offerSpecifics.buyerAgentCompensation,
      originalValue: contract.offerSpecifics.buyerAgentCompensation,
      category: 'price',
      subcategory: 'commissions',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        min: 0,
        max: 10,
      },
    });
  }

  // Earnest Money Deposits
  if ((contract.timeline as any)?.depositOption) {
    clauses.push({
      id: 'deposit-option',
      title: 'Deposit Option',
      description: 'Type of earnest money deposit structure',
      value: (contract.timeline as any).depositOption,
      originalValue: (contract.timeline as any).depositOption,
      category: 'price',
      subcategory: 'deposits',
      isNegotiable: true,
      isRequired: true,
      source: 'timeline',
      validation: {
        options: ['single', 'multiple', 'none'],
      },
    });
  }

  // First Deposit Amount
  if ((contract.timeline as any)?.deposit1Days) {
    clauses.push({
      id: 'first-deposit-amount',
      title: 'First Deposit Amount',
      description: 'Amount of the first earnest money deposit',
      value: (contract.timeline as any).deposit1Days,
      originalValue: (contract.timeline as any).deposit1Days,
      category: 'price',
      subcategory: 'deposits',
      isNegotiable: true,
      isRequired: false,
      source: 'timeline',
      validation: {
        min: 0,
      },
    });
  }

  // Second Deposit Amount
  if ((contract.timeline as any)?.deposit2Days) {
    clauses.push({
      id: 'second-deposit-amount',
      title: 'Second Deposit Amount',
      description: 'Amount of the second earnest money deposit',
      value: (contract.timeline as any).deposit2Days,
      originalValue: (contract.timeline as any).deposit2Days,
      category: 'price',
      subcategory: 'deposits',
      isNegotiable: true,
      isRequired: false,
      source: 'timeline',
      validation: {
        min: 0,
      },
    });
  }

  return clauses;
}

/**
 * Extract comprehensive timeline-related clauses
 */
function extractComprehensiveTimelineClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Closing Date - Use offerSpecifics as source of truth
  if (
    contract.offerSpecifics?.closingDate ||
    (contract.timeline as any)?.closingDate
  ) {
    const closingDate =
      contract.offerSpecifics?.closingDate ||
      (contract.timeline as any)?.closingDate;
    clauses.push({
      id: 'closing-date',
      title: 'Closing Date',
      description: 'The proposed closing date for the transaction',
      value: closingDate,
      originalValue: closingDate,
      category: 'timeline',
      subcategory: 'closing',
      isNegotiable: true,
      isRequired: true,
      source: 'timeline',
      validation: {
        required: true,
      },
    });
  }

  // Offer Good Until Date
  if (contract.timeline?.offerGoodUntillDate) {
    clauses.push({
      id: 'offer-expiration',
      title: 'Offer Expiration Date',
      description: 'Date when this offer expires',
      value: contract.timeline.offerGoodUntillDate,
      originalValue: contract.timeline.offerGoodUntillDate,
      category: 'timeline',
      subcategory: 'closing',
      isNegotiable: true,
      isRequired: false,
      source: 'timeline',
      validation: {
        required: true,
      },
    });
  }

  // Financing Commitment Date
  if (contract.offerSpecifics?.financingCommittmentDate) {
    clauses.push({
      id: 'financing-commitment-date',
      title: 'Financing Commitment Date',
      description: 'Date by which financing must be secured',
      value: contract.offerSpecifics.financingCommittmentDate,
      originalValue: contract.offerSpecifics.financingCommittmentDate,
      category: 'timeline',
      subcategory: 'financing_commitment',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        required: true,
      },
    });
  }

  // Appraisal Days (if negotiable)
  if (
    contract.timeline?.enableBuyerNegotiateAppraisalDays &&
    contract.timeline?.buyerNegotiateAppraisalDays
  ) {
    clauses.push({
      id: 'appraisal-days',
      title: 'Appraisal Days',
      description: 'Number of days for appraisal completion',
      value: contract.timeline.buyerNegotiateAppraisalDays,
      originalValue: contract.timeline.buyerNegotiateAppraisalDays,
      category: 'timeline',
      subcategory: 'appraisal',
      isNegotiable: true,
      isRequired: false,
      source: 'timeline',
      validation: {
        min: 1,
        max: 30,
      },
    });
  }

  return clauses;
}

/**
 * Extract comprehensive contingency-related clauses
 */
function extractComprehensiveContingencyClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Financing Contingency
  if (contract.agentContingency?.financingContingency) {
    clauses.push({
      id: 'financing-contingency',
      title: 'Financing Contingency',
      description: 'Whether the offer is contingent on financing',
      value: contract.agentContingency.financingContingency,
      originalValue: contract.agentContingency.financingContingency,
      category: 'contingency',
      subcategory: 'financing',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        options: ['yes', 'no', 'waived'],
      },
    });
  }

  // Financing Contingency Type
  if (contract.agentContingency?.financingContingencyType) {
    clauses.push({
      id: 'financing-contingency-type',
      title: 'Financing Contingency Type',
      description: 'Type of financing contingency',
      value: contract.agentContingency.financingContingencyType,
      originalValue: contract.agentContingency.financingContingencyType,
      category: 'contingency',
      subcategory: 'financing',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        options: contractFinancingContingencyTypes.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  // Appraisal Contingency
  if (contract.agentContingency?.appraisalContingency) {
    clauses.push({
      id: 'appraisal-contingency',
      title: 'Appraisal Contingency',
      description: 'Whether the offer is contingent on appraisal',
      value: contract.agentContingency.appraisalContingency,
      originalValue: contract.agentContingency.appraisalContingency,
      category: 'contingency',
      subcategory: 'appraisal',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        options: ['yes', 'no', 'waived'],
      },
    });
  }

  // Appraisal Days
  if (contract.agentContingency?.daysFromExecutionDate) {
    clauses.push({
      id: 'appraisal-days-from-execution',
      title: 'Appraisal Days from Execution',
      description: 'Number of days from execution to complete appraisal',
      value: contract.agentContingency.daysFromExecutionDate,
      originalValue: contract.agentContingency.daysFromExecutionDate,
      category: 'contingency',
      subcategory: 'appraisal',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        min: 1,
        max: 30,
      },
    });
  }

  // Inspection Contingency
  if (contract.agentContingency?.inspectionContingency) {
    clauses.push({
      id: 'inspection-contingency',
      title: 'Inspection Contingency',
      description: 'Whether the offer is contingent on inspection',
      value: contract.agentContingency.inspectionContingency,
      originalValue: contract.agentContingency.inspectionContingency,
      category: 'contingency',
      subcategory: 'inspection',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        options: contractInspectionContingencies.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  // Post-Close Occupancy
  if (contract.agentContingency?.postCloseOccupancy) {
    clauses.push({
      id: 'post-close-occupancy',
      title: 'Post-Close Occupancy',
      description: 'Whether seller can remain in property after closing',
      value: contract.agentContingency.postCloseOccupancy,
      originalValue: contract.agentContingency.postCloseOccupancy,
      category: 'contingency',
      subcategory: 'occupancy',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        options: ['yes', 'no'],
      },
    });
  }

  // Post-Close Occupancy Days
  if (contract.agentContingency?.postCloseOccupancyDays) {
    clauses.push({
      id: 'post-close-occupancy-days',
      title: 'Post-Close Occupancy Days',
      description: 'Number of days seller can remain after closing',
      value: contract.agentContingency.postCloseOccupancyDays,
      originalValue: contract.agentContingency.postCloseOccupancyDays,
      category: 'contingency',
      subcategory: 'occupancy',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        min: 0,
        max: 90,
      },
    });
  }

  // Post-Close Occupancy Rent
  if (contract.agentContingency?.postCloseOccupancyRent) {
    clauses.push({
      id: 'post-close-occupancy-rent',
      title: 'Post-Close Occupancy Rent',
      description: 'Daily rent for post-close occupancy',
      value: contract.agentContingency.postCloseOccupancyRent,
      originalValue: contract.agentContingency.postCloseOccupancyRent,
      category: 'contingency',
      subcategory: 'occupancy',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        min: 0,
      },
    });
  }

  return clauses;
}

/**
 * Extract comprehensive inspection-related clauses
 */
function extractComprehensiveInspectionClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Property Condition
  if (contract.offerSpecifics?.propertyCondition) {
    clauses.push({
      id: 'property-condition',
      title: 'Property Condition',
      description: 'Expected condition of the property',
      value: contract.offerSpecifics.propertyCondition,
      originalValue: contract.offerSpecifics.propertyCondition,
      category: 'inspection',
      subcategory: 'property_condition',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
      validation: {
        options: contractPropertyConditions.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  // Inspection Waiver
  if (contract.thirdPartyInspection?.buyerWaivesInspection !== undefined) {
    clauses.push({
      id: 'inspection-waiver',
      title: 'Inspection Waiver',
      description: 'Whether buyer waives the right to inspection',
      value: contract.thirdPartyInspection.buyerWaivesInspection,
      originalValue: contract.thirdPartyInspection.buyerWaivesInspection,
      category: 'inspection',
      subcategory: 'home_inspection',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
      validation: {
        type: 'boolean',
      },
    });
  }

  // Inspection Company
  if (contract.thirdPartyInspection?.inspectionCompany) {
    clauses.push({
      id: 'inspection-company',
      title: 'Inspection Company',
      description: 'Preferred inspection company',
      value: contract.thirdPartyInspection.inspectionCompany,
      originalValue: contract.thirdPartyInspection.inspectionCompany,
      category: 'inspection',
      subcategory: 'home_inspection',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
    });
  }

  // Inspection Report Completed By
  if (contract.thirdPartyInspection?.inspectionReportCompletedBy) {
    clauses.push({
      id: 'inspection-report-completed-by',
      title: 'Inspection Report Completed By',
      description: 'When the inspection report should be completed by',
      value: contract.thirdPartyInspection.inspectionReportCompletedBy,
      originalValue: contract.thirdPartyInspection.inspectionReportCompletedBy,
      category: 'inspection',
      subcategory: 'home_inspection',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
    });
  }

  // Negotiation Days from Discovery
  if (contract.thirdPartyInspection?.negotiationDaysFromDiscovery) {
    clauses.push({
      id: 'negotiation-days-from-discovery',
      title: 'Negotiation Days from Discovery',
      description: 'Days to negotiate after inspection discovery',
      value: contract.thirdPartyInspection.negotiationDaysFromDiscovery,
      originalValue: contract.thirdPartyInspection.negotiationDaysFromDiscovery,
      category: 'inspection',
      subcategory: 'inspection_terms',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
      validation: {
        min: 1,
        max: 14,
      },
    });
  }

  // Copy of Inspection
  if (contract.thirdPartyInspection?.copyOfInspection) {
    clauses.push({
      id: 'copy-of-inspection',
      title: 'Copy of Inspection',
      description: 'Whether seller receives a copy of inspection report',
      value: contract.thirdPartyInspection.copyOfInspection,
      originalValue: contract.thirdPartyInspection.copyOfInspection,
      category: 'inspection',
      subcategory: 'inspection_terms',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
      validation: {
        options: contractCopyOfInspections.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  // Presence Option
  if (contract.thirdPartyInspection?.presenceOption) {
    clauses.push({
      id: 'inspection-presence-option',
      title: 'Inspection Presence Option',
      description: 'Who can be present during inspection',
      value: contract.thirdPartyInspection.presenceOption,
      originalValue: contract.thirdPartyInspection.presenceOption,
      category: 'inspection',
      subcategory: 'inspection_terms',
      isNegotiable: true,
      isRequired: false,
      source: 'thirdPartyInspection',
      validation: {
        options: contractInspectionPresenceOptions.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      },
    });
  }

  return clauses;
}

/**
 * Extract comprehensive occupancy-related clauses
 */
function extractComprehensiveOccupancyClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Post-Close Occupancy Terms
  if (contract.agentContingency?.postCloseOccupancyTerms) {
    clauses.push({
      id: 'post-close-occupancy-terms',
      title: 'Post-Close Occupancy Terms',
      description: 'Specific terms for post-close occupancy',
      value: contract.agentContingency.postCloseOccupancyTerms,
      originalValue: contract.agentContingency.postCloseOccupancyTerms,
      category: 'occupancy',
      subcategory: 'post_close_occupancy',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
    });
  }

  // Post-Close Occupancy Deposit
  if (contract.agentContingency?.postCloseOccupancyDeposit) {
    clauses.push({
      id: 'post-close-occupancy-deposit',
      title: 'Post-Close Occupancy Deposit',
      description: 'Security deposit for post-close occupancy',
      value: contract.agentContingency.postCloseOccupancyDeposit,
      originalValue: contract.agentContingency.postCloseOccupancyDeposit,
      category: 'occupancy',
      subcategory: 'post_close_occupancy',
      isNegotiable: true,
      isRequired: false,
      source: 'agentContingency',
      validation: {
        min: 0,
      },
    });
  }

  return clauses;
}

/**
 * Extract comprehensive property-related clauses
 */
function extractComprehensivePropertyClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Personal Property
  if (contract.offerSpecifics?.personalProperty) {
    clauses.push({
      id: 'personal-property',
      title: 'Personal Property',
      description: 'Personal property included with the sale',
      value: contract.offerSpecifics.personalProperty,
      originalValue: contract.offerSpecifics.personalProperty,
      category: 'property',
      subcategory: 'personal_property',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
    });
  }

  // Leased Property
  if (contract.offerSpecifics?.leasedProperty) {
    clauses.push({
      id: 'leased-property',
      title: 'Leased Property',
      description: 'Leased property included with the sale',
      value: contract.offerSpecifics.leasedProperty,
      originalValue: contract.offerSpecifics.leasedProperty,
      category: 'property',
      subcategory: 'personal_property',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
    });
  }

  // Excluded Personal Items
  if (contract.offerSpecifics?.excludedPersonalItem) {
    clauses.push({
      id: 'excluded-personal-items',
      title: 'Excluded Personal Items',
      description: 'Personal property excluded from the sale',
      value: contract.offerSpecifics.excludedPersonalItem,
      originalValue: contract.offerSpecifics.excludedPersonalItem,
      category: 'property',
      subcategory: 'exclusions',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
    });
  }

  // Subject To
  if (contract.offerSpecifics?.subjectTo) {
    clauses.push({
      id: 'subject-to',
      title: 'Subject To',
      description: 'Conditions the offer is subject to',
      value: contract.offerSpecifics.subjectTo,
      originalValue: contract.offerSpecifics.subjectTo,
      category: 'property',
      subcategory: 'property_info',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
    });
  }

  // Additional Subject To
  if (contract.offerSpecifics?.additionalSubjectTo) {
    clauses.push({
      id: 'additional-subject-to',
      title: 'Additional Subject To',
      description: 'Additional conditions the offer is subject to',
      value: contract.offerSpecifics.additionalSubjectTo,
      originalValue: contract.offerSpecifics.additionalSubjectTo,
      category: 'property',
      subcategory: 'property_info',
      isNegotiable: true,
      isRequired: false,
      source: 'offerSpecifics',
    });
  }

  return clauses;
}

/**
 * Extract comprehensive legal and documentation clauses
 */
function extractComprehensiveLegalClauses(
  contract: Contract
): ComprehensiveClauseData[] {
  const clauses: ComprehensiveClauseData[] = [];

  // Legal Description
  if (contract.agentSellerInfo?.legalDescription) {
    clauses.push({
      id: 'legal-description',
      title: 'Legal Description',
      description: 'Legal description of the property',
      value: contract.agentSellerInfo.legalDescription,
      originalValue: contract.agentSellerInfo.legalDescription,
      category: 'legal',
      subcategory: 'legal_description',
      isNegotiable: false, // Usually not negotiable
      isRequired: false,
      source: 'agentSellerInfo',
    });
  }

  // Zoning
  if (contract.agentSellerInfo?.zoning) {
    clauses.push({
      id: 'zoning',
      title: 'Zoning',
      description: 'Zoning classification of the property',
      value: contract.agentSellerInfo.zoning,
      originalValue: contract.agentSellerInfo.zoning,
      category: 'legal',
      subcategory: 'legal_description',
      isNegotiable: false, // Usually not negotiable
      isRequired: false,
      source: 'agentSellerInfo',
    });
  }

  // HOA Declaration
  if ((contract.agentSellerInfo as any)?.hoaDeclaration) {
    clauses.push({
      id: 'hoa-declaration',
      title: 'HOA Declaration',
      description: 'HOA declaration requirements',
      value: (contract.agentSellerInfo as any).hoaDeclaration,
      originalValue: (contract.agentSellerInfo as any).hoaDeclaration,
      category: 'legal',
      subcategory: 'documentation',
      isNegotiable: false, // Usually not negotiable
      isRequired: false,
      source: 'agentSellerInfo',
    });
  }

  // Condo Declaration
  if ((contract.agentSellerInfo as any)?.condoDeclaration) {
    clauses.push({
      id: 'condo-declaration',
      title: 'Condo Declaration',
      description: 'Condo declaration requirements',
      value: (contract.agentSellerInfo as any).condoDeclaration,
      originalValue: (contract.agentSellerInfo as any).condoDeclaration,
      category: 'legal',
      subcategory: 'documentation',
      isNegotiable: false, // Usually not negotiable
      isRequired: false,
      source: 'agentSellerInfo',
    });
  }

  // Seller Disclosures
  if (
    contract.agentSellerInfo?.sellerDisclosures &&
    contract.agentSellerInfo.sellerDisclosures.length > 0
  ) {
    clauses.push({
      id: 'seller-disclosures',
      title: 'Seller Disclosures',
      description: 'Required seller disclosures',
      value: contract.agentSellerInfo.sellerDisclosures,
      originalValue: contract.agentSellerInfo.sellerDisclosures,
      category: 'legal',
      subcategory: 'disclosures',
      isNegotiable: false, // Usually not negotiable
      isRequired: false,
      source: 'agentSellerInfo',
    });
  }

  return clauses;
}

/**
 * Enhance clauses with hierarchical IDs and properties
 */
export function extractComprehensiveContractClausesWithIds(
  contract: Contract,
  contractId: string
): ComprehensiveClauseCategory[] {
  // Safety checks
  if (!contractId) {
    console.warn(
      'extractComprehensiveContractClausesWithIds - Empty contractId provided, returning empty categories'
    );
    return [];
  }

  if (!contract) {
    console.warn(
      'extractComprehensiveContractClausesWithIds - No contract data provided, returning empty categories'
    );
    return [];
  }

  console.log(
    'extractComprehensiveContractClausesWithIds - Processing contract:',
    {
      contractId,
      contractType: typeof contract,
      contractKeys: Object.keys(contract),
      hasOfferSpecifics: !!contract.offerSpecifics,
      offerSpecificsKeys: contract.offerSpecifics
        ? Object.keys(contract.offerSpecifics)
        : 'N/A',
    }
  );

  const baseCategories = extractComprehensiveContractClauses(contract);

  // Add hierarchical IDs, properties, and negotiation status to each clause
  return baseCategories.map((category) => ({
    ...category,
    clauses: category.clauses.map((clause) => {
      try {
        const properties = generateClauseIdWithProperties(
          contractId,
          clause.id,
          {
            status: 'original',
            proposedBy: 'buyer',
            lastModifiedBy: 'buyer',
            visibleTo: ['buyer', 'seller', 'agent'],
            hiddenFrom: [],
            canEdit: ['buyer', 'agent'],
            canCounter: ['seller', 'agent'],
            canAccept: ['seller'],
            canReject: ['seller'],
            displayInNegotiation: true,
            displayInContract: true,
            displayInCounterOffer: false,
            requiresAttention: false,
            isUrgent: false,
            requiresRatification: false,
            isAddendum: false,
            isExtension: false,
            isRepairRequest: false,
          }
        );

        return {
          ...clause,
          hierarchicalId: properties.hierarchicalId,
          properties,
        };
      } catch (error) {
        console.warn(
          `Error generating clause properties for ${clause.id}:`,
          error
        );
        return {
          ...clause,
          hierarchicalId: `${contractId}-CL-${clause.id}`,
          properties: undefined,
        };
      }
    }),
  }));
}
